<template>
  <div class="wrapper">
    <div class="main">
      <div v-if="emailLoading" class="container-fluid text-center">
        <div class="row mt-5 mb-4 pt-5">
          <div class="col-md-8 ml-auto mr-auto text-center mt-5 h-100vh">
            <h1 class="title">Loading letter&hellip; <br /><spinner></spinner></h1>
          </div>
        </div>
      </div>
      <div v-else>
        <div class="container-fluid">
          <div class="row mt-5 mb-4 pt-5">
            <div class="col-md-10 ml-auto mr-auto text-center mt-5">
              <h1 class="title">Preston Mosques for Al-Aqsa</h1>
              <br />
              <h4 class="desc">
                Send a letter to your MP regarding the closure of Al-Aqsa Mosque.
              </h4>
              <p class="text-muted mt-3 mb-0">
                <strong
                  >Al-Aqsa Mosque has been closed for {{ closureDuration }}.</strong
                >
                Your voice is urgently needed: contact Mark Hendrick, MP for
                Preston, and ask him to press for the immediate reopening of the
                third holiest site in Islam.
                <strong>It takes less than 10 seconds.</strong>
                Enter your full name below so they appear in the pre-written
                letter, then click &ldquo;Open in email&rdquo; to send it from
                your own account.
              </p>
              <br />
            </div>
          </div>
          <div class="row my-5">
            <div class="col-md-10 mx-auto">
              <div
                v-if="emailLoadError"
                class="alert alert-danger mb-3"
                role="alert"
              >
                Could not load the latest letter text from the server. Showing a
                backup version. If this persists, the host may need to allow CORS
                for openmasjid.io on alaqsa.theoccupiedlands.com, or open this
                page directly on OpenMasjid.
              </div>
              <form
                ref="form"
                role="form"
                class="card bg-secondary p-3"
                @submit.prevent="handleSubmit"
              >
                <div class="card-body">
                  <div
                    v-if="showNameError"
                    ref="nameErrorAlert"
                    class="alert alert-warning mb-3"
                    role="alert"
                  >
                    Please enter your full name. It will appear in the letter to
                    your MP.
                  </div>
                  <div class="form-group">
                    <label>Full name <span class="text-danger">*</span></label>
                    <base-input
                      v-model="fullName"
                      alternative
                      placeholder="Your full name"
                      v-bind="nameInputBind"
                      :aria-describedby="
                        showNameError ? 'name-error-hint' : undefined
                      "
                      :aria-invalid="showNameError ? 'true' : undefined"
                    ></base-input>
                    <div
                      id="name-error-hint"
                      v-show="showNameError"
                      class="invalid-feedback d-block"
                    >
                      Please enter your full name.
                    </div>
                  </div>
                  <div class="form-group">
                    <label>Subject</label>
                    <base-input
                      v-model="subject"
                      alternative
                      placeholder="Subject"
                    ></base-input>
                  </div>
                  <div class="form-group">
                    <label>Letter</label>
                    <textarea
                      :value="letterBody"
                      class="form-control form-control-alternative"
                      rows="22"
                      readonly
                    ></textarea>
                  </div>
                  <div class="row">
                    <div class="col-md-6 ml-auto">
                      <base-button
                        nativeType="submit"
                        type="primary"
                        class="pull-right"
                      >
                        Open in email
                      </base-button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Spinner from "@/components/Spinner";

const MARK_HENDRICK_EMAIL = "mark.hendrick.mp@parliament.uk";
const MP_NAME = "Mark Hendrick";
const EMAIL_JSON_URL =
  "https://alaqsa.theoccupiedlands.com/email.json";

/** Strip address sentence from JSON body paragraphs */
const ADDRESS_CLAUSE =
  / I live at <constituent-address-line1><constituent-postcode>\./g;

// Date and time when the closure began (update if the start date is corrected).
const CLOSURE_START_DATE = new Date("2026-02-27T00:00:00.000Z");

function getClosureDayCount() {
  const now = Date.now();
  const start = CLOSURE_START_DATE.getTime();
  const diffMs = Math.max(0, now - start);
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

function getClosureDurationText() {
  const days = getClosureDayCount();
  return `${days} day${days !== 1 ? "s" : ""}`;
}

function ordinalDay(n) {
  const v = n % 100;
  if (v >= 11 && v <= 13) {
    return `${n}th`;
  }
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

const FALLBACK_SUBJECT = "The Closure of Al-Aqsa Mosque";

const LETTER_TEMPLATE_FALLBACK = `Dear Mark Hendrick,

My name is [Your Name], and I am one of your constituents of Preston.

I am writing regarding the closure of Al-Aqsa Mosque by the Israeli government, which, as of writing this, has been closed for [Closure Duration]. Al-Aqsa Mosque is the third holiest site in Islam, and despite the Israeli government's various trespasses and desecrations on the site over the years, this action must rank among the most condemnable. Muslims across the globe are observing the holy month of Ramadhan, a period dedicated to prayer, reflection, and communal worship, making the continued closure particularly distressing.

Preventing worshippers from accessing such a significant religious site during this sacred time raises serious concerns about religious freedom and the protection of holy places. For many Muslims, the ability to pray at Al-Aqsa during Ramadhan holds immense spiritual importance, and the current restrictions have caused widespread alarm and hurt within Muslim communities worldwide, including here in the United Kingdom.

The Israeli government unilaterally closing Al-Aqsa Mosque demonstrates a profound lack of care and respect not only for Muslims residing within Israel and the occupied Palestinian territories, but also for Muslims globally, particularly those who travel, often at great personal expense, to the occupied Palestinian territories specifically to worship and spend time at Al-Aqsa Mosque.

I urge you to raise this issue in Parliament and with the Foreign, Commonwealth and Development Office, and to press the UK Government to call for the immediate reopening of Al-Aqsa Mosque to worshippers and for the protection of freedom of worship at it.

The UK has long stated its commitment to upholding international law, protecting religious freedoms, and supporting a just and lasting peace. Speaking out against actions that restrict access to religious sites would be consistent with those commitments.

I would appreciate your response on what steps you and the Government intend to take regarding this matter.

[Your Name]`;

function normalizeEmailBodyParagraphs(body) {
  if (!Array.isArray(body)) {
    return [];
  }
  return body
    .map((p) =>
      typeof p === "string"
        ? p.replace(ADDRESS_CLAUSE, "").trim()
        : ""
    )
    .filter(Boolean);
}

export default {
  name: "PrestonMosquesForAlAqsa",
  components: { Spinner },
  bodyClass: "contact-page",
  data() {
    return {
      fullName: "",
      subject: FALLBACK_SUBJECT,
      showNameError: false,
      emailLoading: true,
      emailLoadError: false,
      /** Processed paragraphs from JSON (address line removed); empty = use fallback */
      emailBodyTemplate: null,
      useFetchedLetter: false,
    };
  },
  watch: {
    fullName() {
      if (this.fullName.trim()) {
        this.showNameError = false;
      }
    },
  },
  computed: {
    nameInputBind() {
      return this.showNameError ? { valid: false } : {};
    },
    closureDuration() {
      return getClosureDurationText();
    },
    letterBody() {
      const name = this.fullName.trim() || "[Your name]";
      if (
        this.useFetchedLetter &&
        this.emailBodyTemplate &&
        this.emailBodyTemplate.length
      ) {
        let text = this.emailBodyTemplate.join("\n\n");
        text = text.replace(/<mp-name>/g, MP_NAME);
        text = text.replace(/<constituent-name>/g, name);
        text = text.replace(
          /<days-since-closure>/g,
          ordinalDay(getClosureDayCount())
        );
        return `${text}\n\n${name}`;
      }
      let text = LETTER_TEMPLATE_FALLBACK;
      text = text.replace(/\[Your Name\]/g, name);
      text = text.replace("[Closure Duration]", getClosureDurationText());
      return text;
    },
    mailtoUrl() {
      const encodedSubject = encodeURIComponent(this.subject);
      const encodedBody = encodeURIComponent(this.letterBody);
      return `mailto:${MARK_HENDRICK_EMAIL}?subject=${encodedSubject}&body=${encodedBody}`;
    },
  },
  async mounted() {
    await this.loadEmailTemplate();
  },
  methods: {
    async loadEmailTemplate() {
      this.emailLoading = true;
      this.emailLoadError = false;
      try {
        const res = await fetch(EMAIL_JSON_URL, {
          credentials: "omit",
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = await res.json();
        if (!data || typeof data.subject !== "string" || !Array.isArray(data.body)) {
          throw new Error("Invalid JSON shape");
        }
        const paragraphs = normalizeEmailBodyParagraphs(data.body);
        if (!paragraphs.length) {
          throw new Error("Empty body");
        }
        this.emailBodyTemplate = paragraphs;
        this.subject = data.subject.trim() || FALLBACK_SUBJECT;
        this.useFetchedLetter = true;
      } catch (e) {
        console.warn("Preston letter fetch failed:", e);
        this.emailLoadError = true;
        this.useFetchedLetter = false;
        this.emailBodyTemplate = null;
        this.subject = FALLBACK_SUBJECT;
      } finally {
        this.emailLoading = false;
      }
    },
    handleSubmit() {
      if (!this.fullName.trim()) {
        this.showNameError = true;
        this.$nextTick(() => {
          const el = this.$refs.nameErrorAlert;
          if (el && el.scrollIntoView) {
            el.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }
        });
        return;
      }
      this.openMailtoClient();
    },
    openMailtoClient() {
      const url = this.mailtoUrl;
      if (window.self !== window.top) {
        const a = document.createElement("a");
        a.setAttribute("href", url);
        a.setAttribute("target", "_top");
        a.setAttribute("rel", "noopener noreferrer");
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        window.location.href = url;
      }
    },
  },
};
</script>

<style scoped></style>
